/**
 * PrimeReact locale bridge.
 *
 * PrimeReact ships its own English strings for the states it renders itself —
 * the "No available options" a Dropdown shows when its options array is empty,
 * the "No results found" a filter shows, the paginator's report template. None
 * of those pass through our i18n layer, so they stayed English in Arabic even
 * though every label around them was translated (CVA-4475, and the dropdown
 * half of CVA-4532).
 *
 * The fix is to register a PrimeReact locale per app language, built from our
 * own translation keys, and re-register it whenever the language changes or the
 * CDN bundle finishes loading (the strings are empty until it does).
 *
 * We read from i18next directly rather than from ../i18n/i18n so this module can
 * be imported at bootstrap without a circular import.
 */
import { addLocale, locale } from 'primereact/api';
import i18next from 'i18next';

const t = (key: string, fallback: string): string =>
  String(i18next.t(key, { defaultValue: fallback }));

export function applyPrimeReactLocale(code: string): void {
  if (!code) return;
  addLocale(code, {
    emptyMessage: t('common.no_available_options', 'No available options'),
    emptyFilterMessage: t('common.no_results_found', 'No results found'),
    choose: t('common.button_choose', 'Choose'),
    upload: t('common.button_upload', 'Upload'),
    cancel: t('common.button_cancel', 'Cancel'),
    clear: t('common.button_clear', 'Clear'),
    apply: t('common.button_apply', 'Apply'),
    weak: t('common.password_weak', 'Weak'),
    medium: t('common.password_medium', 'Medium'),
    strong: t('common.password_strong', 'Strong'),
  });
  locale(code);
}

/** Wire the bridge once, at bootstrap, before React renders. */
export function initPrimeReactLocale(): void {
  const reapply = () => applyPrimeReactLocale(i18next.language);

  // i18next initialises asynchronously (HttpBackend). Calling t() before that
  // resolves returns the English defaultValue, which would freeze the English
  // strings into the locale for the rest of the session — so re-register on
  // every event that can change what t() returns, not just at startup.
  i18next.on('initialized', reapply);
  i18next.on('languageChanged', applyPrimeReactLocale);
  i18next.on('loaded', reapply);
  // No CDN configured (local dev) means 'loaded' never fires; the bundled
  // ar.json is still there, so this path must register too.
  i18next.on('failedLoading', reapply);

  if (i18next.isInitialized) reapply();
}
